import {
    CdkDropList,
    type CdkDrag,
    type CdkDragDrop,
} from '@angular/cdk/drag-drop';
import {
    afterNextRender,
    ElementRef,
    Injector,
    computed,
    viewChild,
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgIcon } from '@ng-icons/core';
import {
    QpxButton,
    QpxDropSurface,
    QpxIcon,
    QpxFormFieldDirectives,
    QpxFormFieldLabel,
    QpxTypography,
} from '@quipex/shared/ui';
import { DescriptionDisclosure } from '../../description-disclosure';
import type { PackDraftRequirement, PackDraftSection } from '../../models';
import { DocumentDragState } from '../../pack-document/document-drag-state';
import { QuippyPackDocument } from '../../pack-document/pack-document';
import { ProposalState } from '../../proposal-state';
import { QuippyPackRequirementHeading } from '../requirement-heading/requirement-heading';
@Component({
    selector: 'qpx-quippy-pack-requirement-editor',
    imports: [
        CdkDropList,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        NgIcon,
        QpxButton,
        QpxDropSurface,
        QpxIcon,
        QpxFormFieldDirectives,
        QpxTypography,
        QpxFormFieldLabel,
        QuippyPackDocument,
        QuippyPackRequirementHeading,
    ],
    templateUrl: './requirement-editor.html',
    styleUrl: './requirement-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'requirement-main min-w-0' },
})
export class QuippyPackRequirementEditor {
    private readonly descriptionDisclosure = inject(DescriptionDisclosure);
    private readonly injector = inject(Injector);
    private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly descriptionInput =
        viewChild<ElementRef<HTMLTextAreaElement>>('descriptionInput');
    protected readonly descriptionVisible = computed(() =>
        this.descriptionDisclosure.keys().has(this.row().id)
    );
    protected readonly draft = inject(ProposalState);
    protected readonly documentDrag = inject(DocumentDragState);
    public readonly smallHeaders = input(false);
    public readonly actionId = input.required<string>();
    public readonly row = input.required<PackDraftRequirement>();
    public readonly group = input.required<PackDraftSection>();
    public readonly rowIndex = input.required<number>();
    public readonly disabled = input(false);
    public readonly changed = output<void>();
    public readonly moved = output<string>();
    protected readonly canDropDocument = (drag: CdkDrag<string>): boolean =>
        this.row().documents.length < 5 ||
        this.row().documents.some((doc) => doc.documentId === drag.data);
    protected showDescription(): void {
        this.keepDescriptionVisible();
        afterNextRender(() => this.descriptionInput()?.nativeElement.focus(), {
            injector: this.injector,
        });
    }
    protected hideDescription(): void {
        this.descriptionDisclosure.hide(this.row().id);
        afterNextRender(
            () =>
                this.element.nativeElement
                    .querySelector<HTMLButtonElement>('button[aria-controls]')
                    ?.focus(),
            { injector: this.injector }
        );
    }
    protected keepDescriptionVisible(): void {
        this.descriptionDisclosure.show(this.row().id);
    }
    protected dropDocument(event: CdkDragDrop<string, string, string>): void {
        if (this.disabled() || !event.isPointerOverContainer) return;
        if (this.draft.moveDocument(event.item.data, this.row().id)) {
            this.moved.emit(this.group().key);
            this.changed.emit();
        }
    }
}
